import { Link } from "react-router-dom";
import {
  CheckCircle2, Star, ScanLine, ShieldCheck, Award, ArrowDown,
} from "lucide-react";
import SEOHead from "@/components/seo/SEOHead";
import { breadcrumbJsonLd } from "@/lib/page-seo";
import PricingTable from "@/components/seo/PricingTable";
import FaqAccordion, { buildFaqJsonLd } from "@/components/seo/FaqAccordion";
import DoctorBlock from "@/components/seo/DoctorBlock";
import KalyanLocalSignals from "@/components/seo/KalyanLocalSignals";
import CTASection from "@/components/seo/CTASection";
import StepsJourney, { type JourneyStep } from "@/components/seo/StepsJourney";
import BeforeAfterCarousel from "@/components/BeforeAfterCarousel";
import ServiceTestimonials from "@/components/seo/ServiceTestimonials";
import ScrollTriggeredVideo from "@/components/landing/ScrollTriggeredVideo";
import { getSeoPage } from "@/lib/seo-content";
import alignersHero from "@/assets/invisalign-aligners-case.jpg";

const VIDEO_SRC = "/videos/invisalign-harsh-story.mp4";
const POSTER_SRC = "/videos/invisalign-harsh-story-poster.jpg";

const STEPS: JourneyStep[] = [
  { phase: "Visit 1", title: "Free 3D iTero scan", desc: "A digital scan of your teeth — no putty, no gagging. Takes 5 minutes." },
  { phase: "Visit 2", title: "ClinCheck simulation", desc: "We show you a 3D preview of your final smile before you commit to anything." },
  { phase: "Week 2–4", title: "Receive your aligners", desc: "A custom set of clear trays arrives directly from Align Technology, USA." },
  { phase: "Every 1–2 weeks", title: "Switch to the next tray", desc: "Wear 22 hrs a day. Brief check-ins every 6–8 weeks at the clinic." },
  { phase: "After treatment", title: "Vivera retainers", desc: "Lifetime-style retainers to lock your new smile in place." },
];

const TRUST_BADGES = [
  { icon: Award, label: "Certified Invisalign Provider" },
  { icon: ScanLine, label: "iTero 3D scan included" },
  { icon: Star, label: "4.9★ on Google · 500+ reviews" },
  { icon: ShieldCheck, label: "15+ years · 10,000+ smiles" },
];

const WHY_BULLETS = [
  "Align-certified provider — verified on invisalign.com/find-a-doctor",
  "Full ClinCheck Pro access — see your final smile before you pay",
  "iTero Lumina 3D scanning — no putty impressions, ever",
  "All 6 official Invisalign packages — Express to Comprehensive to Teen",
  "5 years of free refinements with the Comprehensive package",
  "No-cost EMI on Invisalign — 6, 9 or 12 months",
];

const InvisalignMobileLandingV2 = () => {
  const cost = getSeoPage("invisalign-cost-kalyan");
  const faqs = cost ? cost.faqs : [];
  const pricingRows = cost?.pricing?.rows ?? [];

  const whatsappMessage = "Hi Mutha's Advanced Dental Care, I saw your Invisalign v2 mobile page and want to book a free 3D smile simulation.";

  const jsonLd: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: "Harsh's Invisalign smile transformation at Mutha's Advanced Dental Care Kalyan",
      description: "Real Invisalign patient story from Mutha's Advanced Dental Care, Kalyan.",
      thumbnailUrl: ["https://muthaadvanceddentalcare.in" + POSTER_SRC],
      uploadDate: "2026-04-30",
      contentUrl: "https://muthaadvanceddentalcare.in" + VIDEO_SRC,
      duration: "PT50S",
    },
    buildFaqJsonLd(faqs),
    breadcrumbJsonLd([{ name: "Invisalign Kalyan", path: "/lp/invisalign-mobile-v2" }]),
  ];

  return (
    <>
      <SEOHead
        title="Invisalign in Kalyan | Free 3D Smile Simulation | Mutha's Advanced Dental Care"
        description="Certified Invisalign provider in Kalyan. See real patient stories, honest pricing, and book a free 3D iTero scan with Dr. Khushboo Mutha at Mutha's Advanced Dental Care."
        canonical="/lp/invisalign-mobile-v2"
        ogImage={"https://muthaadvanceddentalcare.in" + POSTER_SRC}
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full max-w-[480px] bg-background">
        {/* SLIDE 1 — Text intro hero (no video) */}
        <section className="relative min-h-[88vh] flex flex-col justify-between px-6 py-10 overflow-hidden">
          <img
            src={alignersHero}
            alt="Invisalign clear aligners in case held in hand"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black/85" />
          <div className="relative">
            <span className="inline-block bg-primary/10 text-primary text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full mb-6">
              Real Patient Story · Invisalign
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-4 text-white drop-shadow-lg">
              Invisalign in Kalyan — <span className="text-gold">Real Stories, Real Smiles.</span>
            </h1>
            <p className="text-white/90 text-base leading-relaxed drop-shadow">
              Watch how Harsh got his confident, aesthetic smile with Invisalign at Mutha's Advanced Dental Care.
              Certified Align provider · 3D iTero scan · ClinCheck preview before you pay.
            </p>

            <ul className="mt-6 space-y-2">
              {TRUST_BADGES.slice(0, 3).map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5 text-sm text-white">
                  <Icon className="h-4 w-4 text-gold shrink-0" />
                  <span className="font-medium drop-shadow">{label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative mt-8">
            <Link
              to="/book-appointment"
              className="block w-full text-center bg-gold text-foreground font-heading font-medium rounded-xl py-3.5 shadow-md hover:opacity-90 transition-opacity"
            >
              Book Free 3D Smile Simulation
            </Link>
            <div className="mt-6 flex flex-col items-center text-white/80 text-xs">
              <span>Scroll to watch Harsh's story</span>
              <ArrowDown className="h-4 w-4 mt-1 animate-bounce" />
            </div>
          </div>
        </section>

        {/* SLIDE 2 — Scroll-triggered video */}
        <ScrollTriggeredVideo
          src={VIDEO_SRC}
          poster={POSTER_SRC}
          eyebrow="Harsh's Story · Invisalign at Mutha's Advanced Dental Care"
        />

        {/* What is Invisalign */}
        <section className="px-5 py-9">
          <span className="text-eyebrow text-primary">What is Invisalign?</span>
          <h2 className="font-heading text-2xl font-light tracking-tight mt-2 mb-4">
            A clear, removable way to straighten your teeth.
          </h2>
          <p className="text-muted-foreground leading-relaxed text-[15px]">
            Invisalign is a series of custom-made transparent aligners worn 22 hours a day. They gradually
            move your teeth into the desired position — removable for eating, brushing and important
            meetings. With over 18 million patients worldwide, it's the gold standard in clear-aligner
            orthodontics.
          </p>
          <p className="text-muted-foreground leading-relaxed text-[15px] mt-3">
            At Mutha's Advanced Dental Care Kalyan, every Invisalign case starts with a 3D iTero scan and a digital ClinCheck
            simulation — so you see your future smile before you commit.
          </p>
        </section>

        {/* Why Mutha's Advanced Dental Care */}
        <section className="px-5 py-9 bg-secondary/30 border-y border-border">
          <span className="text-eyebrow text-primary">Why Mutha's Advanced Dental Care for Invisalign</span>
          <h2 className="font-heading text-2xl font-light tracking-tight mt-2 mb-5">
            Certified, transparent, and proudly local.
          </h2>
          <ul className="space-y-3">
            {WHY_BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15px]">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span className="text-foreground/90 leading-snug">{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {pricingRows.length > 0 && (
          <PricingTable rows={pricingRows} title="Invisalign Cost" />
        )}

        <StepsJourney
          eyebrow="Treatment journey"
          title="From first scan to final smile"
          steps={STEPS}
        />

        <BeforeAfterCarousel
          category="Aligners"
          eyebrow="Before & After"
          heading="Real Invisalign results from Mutha's Advanced Dental Care Kalyan"
        />

        <ServiceTestimonials slug="clear-aligners-kalyan" eyebrow="Invisalign" />

        <DoctorBlock />

        {faqs.length > 0 && <FaqAccordion faqs={faqs} title="Invisalign — Frequently Asked Questions" />}

        <KalyanLocalSignals />

        <CTASection
          title="Ready for your free 3D smile simulation?"
          subtitle="Book a no-pressure consultation with Dr. Khushboo Mutha. We'll do a 3D iTero scan, show you a ClinCheck preview of your final smile, and give you a written quote."
          whatsappMessage={whatsappMessage}
        />

        <div className="h-12" />
      </div>
    </>
  );
};

export default InvisalignMobileLandingV2;